import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Loader, CheckCircle, AlertCircle, UserPlus, Users,
  Plus, Edit2, Trash2
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Conversions USA <-> métrique
const lbsToKg = lbs => lbs ? Math.round(lbs * 0.453592) : '';
const galToL = gal => gal ? Math.round(gal * 3.78541) : '';
const formatKnotsToKmh = (kts) => {
  if (!kts) return '';
  return `${Math.round(kts * 1.852)} km/h`;
};

const Section = ({ title, children }) => (
  <div>
    <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-3 border-b border-gray-700 pb-1">
      {title}
    </h4>
    {children}
  </div>
);

const Label = ({ children }) => (
  <label className="text-gray-400 text-xs mb-1 block">{children}</label>
);

const LookupBadge = ({ lookupStatus }) => {
  if (!lookupStatus) return null;
  const map = {
    loading:  { icon: <Loader size={14} className="animate-spin" />, text: 'Searching FAA…',            color: 'text-blue-400'   },
    found:    { icon: <CheckCircle size={14} />,                      text: 'FAA data imported ✓',      color: 'text-green-400'  },
    notfound: { icon: <AlertCircle size={14} />,                      text: 'Not found — manual entry', color: 'text-yellow-400' },
    error:    { icon: <AlertCircle size={14} />,                      text: 'Lookup error',              color: 'text-red-400'    },
  };
  const { icon, text, color } = map[lookupStatus];
  return (
    <div className={`flex items-center gap-1 text-xs mt-1 ${color}`}>
      {icon} {text}
    </div>
  );
};

// SOURCE LABELS for contacts
const SOURCE_LABELS = {
  VECTOR_VERIFIED: { label: 'VECTOR', color: 'bg-green-600/20 text-green-400 border-green-600/40' },
  AS: { label: 'AS', color: 'bg-blue-600/20 text-blue-400 border-blue-600/40' },
  REGISTRY: { label: 'REGISTRY', color: 'bg-gray-600/20 text-gray-400 border-gray-600/40' },
  MANUAL: { label: 'MANUAL', color: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/40' },
};

const PRIORITY_LABELS = ['Most Likely', '2nd', '3rd', '4th', '5th'];

// ContactsSection component
const ContactsSection = ({
  contacts = [],
  loadingContacts = false,
  showContactForm = false,
  editingContact = null,
  contactForm = {},
  onOpenForm,
  onCloseForm,
  onSave,
  onDelete,
  onContactField,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between border-b border-gray-700 pb-1 mb-3">
        <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider flex items-center gap-1.5">
          <Users size={13} /> Operator / Owner Contacts
        </h4>
        {!showContactForm && (
          <button
            onClick={() => onOpenForm(null)}
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition"
          >
            <Plus size={13} /> Add
          </button>
        )}
      </div>

      {loadingContacts ? (
        <div className="text-gray-500 text-xs flex items-center gap-2 py-2">
          <Loader size={13} className="animate-spin" /> Loading contacts…
        </div>
      ) : contacts.length === 0 && !showContactForm ? (
        <p className="text-gray-600 text-xs italic py-1">
          No contacts yet — click Add to record operator info.
        </p>
      ) : (
        <div className="space-y-2 mb-3">
          {contacts.map((c, idx) => {
            const badge = SOURCE_LABELS[c.source] || SOURCE_LABELS.MANUAL;
            return (
              <div key={c.id} className="bg-gray-700/50 border border-gray-600/50 rounded-lg p-3 relative">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${badge.color}`}>
                    {badge.label}
                  </span>
                  <span className="text-gray-400 text-[10px]">
                    {PRIORITY_LABELS[idx] ?? `${idx + 1}th`}
                  </span>
                  {c.role && <span className="text-gray-500 text-[10px]">· {c.role}</span>}
                  <div className="ml-auto flex gap-1">
                    <button onClick={() => onOpenForm(c)} className="text-gray-400 hover:text-blue-400 transition p-0.5" title="Edit">
                      <Edit2 size={12} />
                    </button>
                    <button onClick={() => onDelete(c.id)} className="text-gray-400 hover:text-red-400 transition p-0.5" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {(c.contact_name || c.company_name) && (
                  <p className="text-white text-xs font-medium">
                    {c.company_name && <span>{c.company_name}</span>}
                    {c.company_name && c.contact_name && <span className="text-gray-400"> · </span>}
                    {c.contact_name}
                  </p>
                )}
                {c.address && (
                  <p className="text-gray-400 text-xs">
                    {c.address}
                    {c.city && `, ${c.city}`}
                    {c.state && `, ${c.state}`}
                    {c.zip && ` ${c.zip}`}
                    {c.country && c.country !== 'US' && ` · ${c.country}`}
                  </p>
                )}
                <div className="flex flex-wrap gap-x-3 mt-1">
                  {c.phone && <span className="text-gray-400 text-xs">{c.phone}</span>}
                  {c.email && <span className="text-gray-400 text-xs">{c.email}</span>}
                </div>
                {c.notes && <p className="text-gray-500 text-xs mt-1 italic">{c.notes}</p>}
              </div>
            );
          })}
        </div>
      )}

      {showContactForm && (
        <div className="bg-gray-700/40 border border-blue-600/30 rounded-lg p-4 space-y-3">
          <h5 className="text-xs font-semibold text-blue-300">
            {editingContact ? 'Edit contact' : 'New contact'}
          </h5>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Source</Label>
              <select
                value={contactForm.source}
                onChange={e => onContactField('source', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                <option value="MANUAL">Manual</option>
                <option value="VECTOR_VERIFIED">Vector Verified</option>
                <option value="AS">AS (Air Segment)</option>
                <option value="REGISTRY">Registry (FAA)</option>
              </select>
            </div>
            <div>
              <Label>Priority</Label>
              <select
                value={contactForm.priority}
                onChange={e => onContactField('priority', parseInt(e.target.value))}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                {[1, 2, 3, 4, 5].map(n => (
                  <option key={n} value={n}>{n} — {PRIORITY_LABELS[n - 1] ?? `${n}th`}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Contact Name</Label>
              <input type="text" placeholder="ex: Michael Stevens" value={contactForm.contact_name}
                onChange={e => onContactField('contact_name', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
            <div>
              <Label>Company</Label>
              <input type="text" placeholder="ex: Gulftex Energy" value={contactForm.company_name}
                onChange={e => onContactField('company_name', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
            <div>
              <Label>Role</Label>
              <select value={contactForm.role} onChange={e => onContactField('role', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                <option value="">— Select role —</option>
                <option value="Operator">Operator</option>
                <option value="Owner">Owner</option>
                <option value="Trustee">Trustee</option>
                <option value="Billing">Billing</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <Label>Street Address</Label>
            <input type="text" value={contactForm.address}
              onChange={e => onContactField('address', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label>City</Label>
              <input type="text" value={contactForm.city}
                onChange={e => onContactField('city', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
            <div>
              <Label>State</Label>
              <input type="text" maxLength={2} placeholder="TX" value={contactForm.state}
                onChange={e => onContactField('state', e.target.value.toUpperCase())}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
            <div>
              <Label>ZIP</Label>
              <input type="text" value={contactForm.zip}
                onChange={e => onContactField('zip', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <input type="tel" value={contactForm.phone}
                onChange={e => onContactField('phone', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
            <div>
              <Label>Email</Label>
              <input type="email" value={contactForm.email}
                onChange={e => onContactField('email', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <textarea rows={2} value={contactForm.notes}
              onChange={e => onContactField('notes', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm resize-none" />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCloseForm}
              className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-xs transition">
              Cancel
            </button>
            <button onClick={onSave}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs transition flex items-center gap-1.5">
              <CheckCircle size={13} />
              {editingContact ? 'Update' : 'Add Contact'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AircraftFormModal = ({
  isOpen,
  editingAircraft,
  formData,
  clients,
  lookupStatus,
  saving,
  // Contact props
  contacts,
  loadingContacts,
  showContactForm,
  editingContact,
  contactForm,
  onClose,
  onFieldChange,
  onTailNumberChange,
  onSubmit,
  onOpenNewOwnerModal,
  onFetchContacts,
  onContactField,
  onOpenContactForm,
  onCloseContactForm,
  onSaveContact,
  onDeleteContact,
}) => {
  useEffect(() => {
    if (isOpen && editingAircraft?.id) {
      onFetchContacts(editingAircraft.id);
    }
  }, [isOpen, editingAircraft, onFetchContacts]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-[95vw] sm:max-w-2xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">

        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">
              {editingAircraft ? `Edit ${editingAircraft.tail_number}` : 'New aircraft'}
            </h3>
            {!editingAircraft && (
              <p className="text-gray-400 text-xs mt-1 hidden sm:block">
                Enter the tail number — FAA data loads automatically.
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1"><X size={20} /></button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-5 flex-1">

          {/* Identification */}
          <Section title="Identification">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Tail Number *</Label>
                <input type="text" placeholder="ex: N12345" value={formData.tail_number}
                  onChange={e => onTailNumberChange(e.target.value)}
                  disabled={!!editingAircraft}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm disabled:opacity-50" />
                <LookupBadge lookupStatus={lookupStatus} />
              </div>
              <div>
                <Label>Manufacturer</Label>
                <input type="text" placeholder="ex: Cessna" value={formData.manufacturer}
                  onChange={e => onFieldChange('manufacturer', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Model</Label>
                <input type="text" placeholder="ex: 172 Skyhawk" value={formData.model}
                  onChange={e => onFieldChange('model', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Aircraft Type</Label>
                <input type="text" placeholder="ex: LandPlane" value={formData.aircraft_type}
                  onChange={e => onFieldChange('aircraft_type', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Year Manufactured</Label>
                <input type="number" placeholder="ex: 2018" value={formData.year_manufactured}
                  onChange={e => onFieldChange('year_manufactured', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Serial Number</Label>
                <input type="text" value={formData.serial_number}
                  onChange={e => onFieldChange('serial_number', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>ICAO Code</Label>
                <input type="text" placeholder="ex: C172" value={formData.icao_code}
                  onChange={e => onFieldChange('icao_code', e.target.value.toUpperCase())}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Callsign</Label>
                <input type="text" value={formData.callsign}
                  onChange={e => onFieldChange('callsign', e.target.value.toUpperCase())}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
            </div>
          </Section>

          {/* Engine & Fuel */}
          <Section title="Engine & Fuel">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Engine Type</Label>
                <input type="text" placeholder="ex: Turbo-prop" value={formData.engine_type}
                  onChange={e => onFieldChange('engine_type', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Engine Count</Label>
                <input type="number" min="1" max="8" value={formData.engine_count}
                  onChange={e => onFieldChange('engine_count', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Engine Manufacturer</Label>
                <input type="text" placeholder="ex: Lycoming" value={formData.engine_manufacturer}
                  onChange={e => onFieldChange('engine_manufacturer', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Engine Model</Label>
                <input type="text" value={formData.engine_model}
                  onChange={e => onFieldChange('engine_model', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Seats</Label>
                <input type="number" min="1" value={formData.seats}
                  onChange={e => onFieldChange('seats', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Fuel Type</Label>
                <select value={formData.fuel_type} onChange={e => onFieldChange('fuel_type', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                  <option value="100LL">100LL (Avgas)</option>
                  <option value="JetA">Jet-A</option>
                  <option value="JetA_plus">Jet-A Plus</option>
                </select>
              </div>
              <div>
                <Label>Fuel Capacity (US gal)</Label>
                <input type="number" min="0" step="0.1" value={formData.fuel_capacity_gallons}
                  onChange={e => onFieldChange('fuel_capacity_gallons', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                {formData.fuel_capacity_gallons > 0 && (
                  <p className="text-gray-500 text-xs mt-0.5">≈ {galToL(parseFloat(formData.fuel_capacity_gallons))} L</p>
                )}
              </div>
              <div>
                <Label>Max Fuel (US gal)</Label>
                <input type="number" min="0" step="0.1" value={formData.max_fuel_gallons}
                  onChange={e => onFieldChange('max_fuel_gallons', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                {formData.max_fuel_gallons > 0 && (
                  <p className="text-gray-500 text-xs mt-0.5">≈ {galToL(parseFloat(formData.max_fuel_gallons))} L</p>
                )}
              </div>
            </div>
          </Section>

          {/* Weights */}
          <Section title="Weights (US lbs)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Empty Weight (lbs)</Label>
                <input type="number" min="0" value={formData.empty_weight_lbs}
                  onChange={e => onFieldChange('empty_weight_lbs', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                {formData.empty_weight_lbs > 0 && (
                  <p className="text-gray-500 text-xs mt-0.5">
                    ≈ {lbsToKg(parseInt(formData.empty_weight_lbs)).toLocaleString()} kg
                  </p>
                )}
              </div>
              <div>
                <Label>Max Takeoff Weight (lbs)</Label>
                <input type="number" min="0" value={formData.max_takeoff_weight_lbs}
                  onChange={e => onFieldChange('max_takeoff_weight_lbs', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                {formData.max_takeoff_weight_lbs > 0 && (
                  <p className="text-gray-500 text-xs mt-0.5">
                    ≈ {lbsToKg(parseInt(formData.max_takeoff_weight_lbs)).toLocaleString()} kg
                  </p>
                )}
              </div>
              <div>
                <Label>Max Landing Weight (lbs)</Label>
                <input type="number" min="0" value={formData.max_landing_weight_lbs}
                  onChange={e => onFieldChange('max_landing_weight_lbs', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                {formData.max_landing_weight_lbs > 0 && (
                  <p className="text-gray-500 text-xs mt-0.5">
                    ≈ {lbsToKg(parseInt(formData.max_landing_weight_lbs)).toLocaleString()} kg
                  </p>
                )}
              </div>
            </div>
          </Section>

          {/* Performance */}
          <Section title="Performance">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Cruise Speed (knots)</Label>
                <input type="number" min="0" max="500" step="1" value={formData.cruise_speed_kts}
                  onChange={e => onFieldChange('cruise_speed_kts', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                {formData.cruise_speed_kts && (
                  <p className="text-gray-500 text-xs mt-0.5">
                    ≈ {formatKnotsToKmh(parseFloat(formData.cruise_speed_kts))}
                  </p>
                )}
              </div>
              <div>
                <Label>Fuel Burn (GPH)</Label>
                <input type="number" min="0" max="200" step="0.1" value={formData.fuel_burn_gph}
                  onChange={e => onFieldChange('fuel_burn_gph', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
                <p className="text-gray-500 text-xs mt-0.5">Gallons per hour at cruise</p>
              </div>
            </div>
          </Section>

          {/* Owner & Position */}
          <Section title="Owner & Position">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Owner (account)</Label>
                <div className="flex gap-2">
                  <select value={formData.owner_id} onChange={e => onFieldChange('owner_id', e.target.value)}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm">
                    <option value="">— No account linked —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.company_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || c.email}
                      </option>
                    ))}
                  </select>
                  <button onClick={onOpenNewOwnerModal}
                    className="flex items-center gap-1.5 px-3 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition whitespace-nowrap flex-shrink-0">
                    <UserPlus size={14} /> New
                  </button>
                </div>
                {!formData.owner_id && (
                  <p className="text-yellow-500/80 text-xs mt-1">Optional — you can link an account later.</p>
                )}
              </div>
              <div className="sm:col-span-2">
                <Label>Owner Name (registry)</Label>
                <input type="text" value={formData.owner_name}
                  onChange={e => onFieldChange('owner_name', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div className="sm:col-span-2">
                <Label>Street Address</Label>
                <input type="text" value={formData.owner_address}
                  onChange={e => onFieldChange('owner_address', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>City</Label>
                <input type="text" value={formData.owner_city}
                  onChange={e => onFieldChange('owner_city', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>State</Label>
                <input type="text" placeholder="TX" maxLength={2} value={formData.owner_state}
                  onChange={e => onFieldChange('owner_state', e.target.value.toUpperCase())}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>ZIP Code</Label>
                <input type="text" placeholder="78006" value={formData.owner_zip}
                  onChange={e => onFieldChange('owner_zip', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Country</Label>
                <input type="text" value={formData.owner_country}
                  onChange={e => onFieldChange('owner_country', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Phone</Label>
                <input type="tel" placeholder="830-336-2518" value={formData.owner_phone}
                  onChange={e => onFieldChange('owner_phone', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Email</Label>
                <input type="email" value={formData.owner_email}
                  onChange={e => onFieldChange('owner_email', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Home Base</Label>
                <input type="text" placeholder="ex: KBOE" value={formData.home_base}
                  onChange={e => onFieldChange('home_base', e.target.value.toUpperCase())}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Parking Spot</Label>
                <input type="text" value={formData.parking_spot}
                  onChange={e => onFieldChange('parking_spot', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
            </div>
          </Section>

          {/* Certificates */}
          <Section title="Certificates">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Airworthiness Date</Label>
                <input type="date" value={formData.airworthiness_date}
                  onChange={e => onFieldChange('airworthiness_date', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
              <div>
                <Label>Certificate Issue Date</Label>
                <input type="date" value={formData.certificate_issue_date}
                  onChange={e => onFieldChange('certificate_issue_date', e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" />
              </div>
            </div>
          </Section>

          {/* Operator / Owner Contacts Section */}
          <Section title="Operator / Owner Contacts">
            <ContactsSection
              contacts={contacts}
              loadingContacts={loadingContacts}
              showContactForm={showContactForm}
              editingContact={editingContact}
              contactForm={contactForm}
              onOpenForm={onOpenContactForm}
              onCloseForm={onCloseContactForm}
              onSave={onSaveContact}
              onDelete={onDeleteContact}
              onContactField={onContactField}
            />
          </Section>

        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-700 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
          <button onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition w-full sm:w-auto">
            Cancel
          </button>
          <button onClick={onSubmit} disabled={saving || !formData.tail_number}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition w-full sm:w-auto">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AircraftFormModal;